"use client";

import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";

import { browserLogger, initializeDatadog } from "./datadog-browser";

const DATADOG_INIT_DELAY_MS = 3000;

type DatadogContextType = {
  isInitialized: boolean;
  logger: typeof browserLogger;
};

const DatadogContext = createContext<DatadogContextType>({
  isInitialized: false,
  logger: browserLogger,
});

export const useDatadog = () => {
  const context = useContext(DatadogContext);
  if (!context) {
    throw new Error("useDatadog must be used within a DatadogProvider");
  }

  return context;
};

type DatadogProviderProps = {
  children: React.ReactNode;
};

export const DatadogProvider: React.FC<DatadogProviderProps> = ({
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const initTimeout = setTimeout(() => {
      try {
        if (initializeDatadog()) {
          setIsInitialized(true);
        }
      } catch (_error) {
        // Datadog initialization must never affect the storefront.
      }
    }, DATADOG_INIT_DELAY_MS);

    return () => clearTimeout(initTimeout);
  }, []);

  useEffect(() => {
    if (!(isInitialized && typeof window !== "undefined")) {
      return;
    }

    const trackPageView = () => {
      browserLogger.rum.addAction("page_view", {
        path: window.location.pathname,
        title: document.title,
      });
    };

    trackPageView();
    window.addEventListener("popstate", trackPageView);
    return () => window.removeEventListener("popstate", trackPageView);
  }, [isInitialized]);

  return (
    <DatadogContext.Provider value={{ isInitialized, logger: browserLogger }}>
      {children}
    </DatadogContext.Provider>
  );
};
